import { useDocuments, usePatchDocument, useRoomsOverview } from "@mimir/api-client"
import type { DocumentSummary } from "@mimir/types"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import { LayoutGrid, List, DoorOpenIcon, FolderOpen, Loader2 } from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/empty-state"
import { DocumentCard } from "@/components/mind-palace/document-card"
import { DocumentListRow } from "@/components/mind-palace/document-list-row"
import { RoomLane } from "@/components/mind-palace/room-lane"
import { RoomDocView } from "@/components/mind-palace/room-doc-view"
import { ClusterView } from "@/components/mind-palace/cluster-view"
import { ClusterIcon } from "@/components/cluster-icon"

export const Route = createFileRoute("/_app/mind-palace/")({
  validateSearch: z.object({
    type: z.string().optional(),
    tag: z.string().optional(),
    layout: z.enum(["grid", "list", "cluster", "rooms"]).optional(),
  }),
  component: MindPalaceIndex,
})

function MindPalaceIndex() {
  const { type: filterType, tag: filterTag, layout = "grid" } = Route.useSearch()
  const navigate = useNavigate()
  const setLayout = (v: string) =>
    navigate({
      to: "/mind-palace",
      search: (prev) => ({ ...prev, layout: v as "grid" | "list" | "cluster" | "rooms" }),
      replace: true,
    })
  const [selectedRoomId, setSelectedRoomId] = useState<string | "unassigned" | null>(null)
  const patchDoc = usePatchDocument()

  const onPinToggle = (doc: DocumentSummary) => {
    patchDoc.mutate({ id: doc.id, patch: { is_pinned: !doc.is_pinned } })
  }

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useDocuments({
    content_type: filterType,
    tag: filterTag,
  })

  // Flatten pages and deduplicate by id — cursor drift can cause the same doc to
  // appear on multiple pages, which would produce duplicate viewTransitionNames
  // and cause Chrome to throw InvalidStateError on view transitions.
  const docs: DocumentSummary[] = data
    ? [...new Map(data.pages.flatMap((p) => p.data).map((d) => [d.id, d])).values()]
    : []

  const { data: roomsOverview } = useRoomsOverview()

  const types = [
    { value: "", label: "All" },
    { value: "text", label: "Text" },
    { value: "link", label: "Links" },
    { value: "file", label: "Files" },
    { value: "audio", label: "Audio" },
  ]

  const layoutOptions = [
    { value: "grid", label: "Grid", icon: LayoutGrid },
    { value: "list", label: "List", icon: List },
    { value: "cluster", label: "Cluster", icon: ClusterIcon },
    { value: "rooms", label: "Rooms", icon: DoorOpenIcon },
  ]

  // Unassigned drill-down (real rooms now navigate to /mind-palace/room/$roomId)
  if (layout === "rooms" && selectedRoomId === "unassigned") {
    const unassignedDocs = docs.filter((d) => !d.room_id)
    return (
      <RoomDocView
        room={null}
        docs={unassignedDocs}
        onBack={() => setSelectedRoomId(null)}
        onDeleted={() => setSelectedRoomId(null)}
        onPinToggle={onPinToggle}
      />
    )
  }

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="flex items-center justify-between gap-4 pb-1 shrink-0 flex-wrap sm:flex-nowrap">
        {layout !== "rooms" && (
          <div className="flex items-center gap-2 overflow-x-auto pb-0.5 flex-1 min-w-0">
            {types.map((t) => {
              const isActive = (t.value === "" && !filterType) || filterType === t.value
              return (
                <Button
                  key={t.label}
                  variant={isActive ? "default" : "outline"}
                  className="rounded-full h-8 px-4 text-xs font-medium"
                  onClick={() =>
                    navigate({
                      to: "/mind-palace",
                      search: { type: t.value || undefined, tag: filterTag },
                    })
                  }
                >
                  {t.label}
                </Button>
              )
            })}
          </div>
        )}
        {layout === "rooms" && <div />}
        <Tabs
          value={layout}
          onValueChange={(v) => {
            setLayout(v)
            setSelectedRoomId(null)
          }}
        >
          <TabsList className="bg-transparent border h-8 p-1">
            {layoutOptions.map((layoutOption) => (
              <TabsTrigger
                key={layoutOption.value}
                value={layoutOption.value}
                className="text-xs data-[state=active]:bg-background data-[state=active]:shadow-sm px-2 sm:px-3 h-6 flex items-center gap-1.5"
              >
                <layoutOption.icon className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden sm:inline">{layoutOption.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <p className="text-sm text-muted-foreground text-center py-10">
            Loading your knowledge...
          </p>
        )}
        {!isLoading && layout !== "rooms" && docs?.length === 0 && (
          <EmptyState
            icon={FolderOpen}
            title="No documents"
            description="No documents found. Try adding one to get started."
          />
        )}
        {layout === "grid" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {docs.map((doc) => (
              <DocumentCard
                key={doc.id}
                doc={doc}
                onClick={() =>
                  navigate({ to: "/mind-palace/$documentId", params: { documentId: doc.id } })
                }
                onPinToggle={() => onPinToggle(doc)}
              />
            ))}
          </div>
        )}
        {layout === "list" && (
          <div className="flex flex-col rounded-xl bg-card overflow-hidden">
            {docs.map((doc) => (
              <DocumentListRow
                key={doc.id}
                doc={doc}
                onClick={() =>
                  navigate({ to: "/mind-palace/$documentId", params: { documentId: doc.id } })
                }
                onPinToggle={() => onPinToggle(doc)}
              />
            ))}
          </div>
        )}
        {layout === "cluster" && <ClusterView />}
        {layout === "rooms" && (
          <div className="flex flex-col gap-8">
            {roomsOverview?.map((room, i) => (
              <RoomLane
                key={room.id}
                room={room}
                index={i}
                onDocClick={(docId) =>
                  navigate({ to: "/mind-palace/$documentId", params: { documentId: docId } })
                }
                onDeleted={() => {}}
              />
            ))}
            {(() => {
              const unassignedCount = docs.filter((d) => !d.room_id).length
              if (unassignedCount === 0) return null
              return (
                <button
                  type="button"
                  onClick={() => setSelectedRoomId("unassigned")}
                  className="text-left rounded-xl border border-dashed border-border p-5 hover:border-primary/40 transition-colors bg-transparent flex items-center gap-3"
                >
                  <FolderOpen className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                  <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                    {unassignedCount}
                  </span>
                </button>
              )
            })()}
          </div>
        )}

        {layout !== "rooms" && layout !== "cluster" && hasNextPage && (
          <div className="flex justify-center pt-4 pb-2">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="gap-2"
            >
              {isFetchingNextPage ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </>
              ) : (
                "Load more"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
